import { useMemo } from 'react';
import type { ChartBinding, ChartBindingValue, ChartBindingValues, CustomChartDefinition } from '@/entities/dashboards';
import { useDashboardChartBindings } from './useDashboardScreenStore';

/** Разрез, переключатель которого показывает именно эта диаграмма. */
export type CustomChartSliceControl = {
  binding: ChartBinding;
  value: ChartBindingValue;
  onChange: (value: ChartBindingValue) => void;
};

/** Места подстановки диаграммы глазами дашборда: чем наполнять и что показывать в шапке. */
export type CustomChartBindingsState = {
  /**
   * Значения мест подстановки для наполнения; `null` — ведущая диаграмма ещё ничего не выбрала,
   * и наполнять эту нечем.
   */
  values: ChartBindingValues | null;
  /**
   * Разрез, переключатель которого показывает эта диаграмма. У разреза, общего для нескольких
   * диаграмм, переключатель один — остальные его не дублируют.
   */
  slice?: CustomChartSliceControl;
  /** Выбор элемента для разбора связанными диаграммами; задан только у ведущей диаграммы */
  onSelectElement?: (value: ChartBindingValue) => void;
  /** Выбранный в этой диаграмме элемент */
  selectedElement?: ChartBindingValue;
};

/**
 * Значения мест подстановки по составу самой диаграммы: разрез — своим начальным значением, место
 * под выбранное — пустым.
 *
 * Ими диаграмма показана там, где дашборда вокруг неё ещё нет, — в предпросмотре правящейся
 * диаграммы: выбирать в ведущей там негде, а пустое значение места честно даёт пустую выборку.
 */
export const chartInitialBindingValues = (chart: {
  bindings?: ChartBinding[];
  sliceValue?: ChartBindingValue;
}): ChartBindingValues => {
  const values: ChartBindingValues = {};
  chart.bindings?.forEach((binding) => {
    values[binding.name] = binding.kind === 'slice' ? (chart.sliceValue ?? binding.initial ?? null) : null;
  });
  return values;
};

/**
 * Разрезы и разбор выбранного на пользовательском дашборде.
 *
 * Разрез — свойство диаграммы, но выбранное значение общее для всех диаграмм, разрезанных разрезом
 * того же имени: переключатель показывает первая из них, а смена значения перенаполняет их все.
 * Диаграммы с разрезом другого имени и без разреза смена не затрагивает.
 *
 * Связанная диаграмма наполняется выбранным в ведущей: пока в ведущей ничего не выбрано, значений
 * для наполнения нет вовсе — карточка показывает подсказку выбрать, а не пустую выборку. Выбор
 * живёт в транзиентном сторе экрана и состав дашборда не меняет.
 *
 * @returns Состояние мест подстановки по идентификатору диаграммы.
 */
export const useCustomChartBindings = (charts: CustomChartDefinition[]): Map<string, CustomChartBindingsState> => {
  const { chartSlices, chartSelections, setChartSlice, toggleChartSelection } = useDashboardChartBindings();

  return useMemo(() => {
    // Переключатель разреза показывает первая диаграмма состава, которая им разрезана: у общего
    // разреза он один на все такие диаграммы.
    const sliceOwner = new Map<string, string>();
    charts.forEach((chart) => {
      chart.bindings
        ?.filter((binding) => binding.kind === 'slice')
        .forEach((binding) => {
          if (!sliceOwner.has(binding.name)) sliceOwner.set(binding.name, chart.id);
        });
    });

    // Ведущая — та, на чей выбор ссылается хоть одна диаграмма состава.
    const leadingChartIds = new Set(
      charts.map((chart) => chart.sourceChartId).filter((id): id is string => Boolean(id)),
    );

    const states = new Map<string, CustomChartBindingsState>();

    charts.forEach((chart) => {
      const bindings = chart.bindings ?? [];
      const values: ChartBindingValues = {};
      let isAwaitingSelection = false;
      let slice: CustomChartBindingsState['slice'];

      bindings.forEach((binding) => {
        if (binding.kind === 'slice') {
          const stored = chartSlices[binding.name];
          const value: ChartBindingValue =
            stored !== undefined ? stored : (chart.sliceValue ?? binding.initial ?? null);
          values[binding.name] = value;
          if (!slice && sliceOwner.get(binding.name) === chart.id) {
            slice = { binding, value, onChange: (next) => setChartSlice(binding.name, next) };
          }
          return;
        }
        // Место под выбранное наполняется только выбором в ведущей диаграмме: без ведущей и без
        // выбора в ней подставлять нечего.
        const selected = chart.sourceChartId ? chartSelections[chart.sourceChartId] : undefined;
        if (selected === undefined) {
          isAwaitingSelection = true;
          return;
        }
        values[binding.name] = selected;
      });

      states.set(chart.id, {
        values: isAwaitingSelection ? null : values,
        slice,
        onSelectElement: leadingChartIds.has(chart.id) ? (value) => toggleChartSelection(chart.id, value) : undefined,
        selectedElement: chartSelections[chart.id],
      });
    });

    return states;
  }, [charts, chartSlices, chartSelections, setChartSlice, toggleChartSelection]);
};
